import { useCallback, useEffect, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { type RovoUserSettings } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { useRovoUserSettings } from '../rovo-user-settings';
import { useUserPreferences } from '../user-preferences';

export type UpdateStoreUserMessagesCallbacks =
	| {
			onSuccess?: ((data: RovoUserSettings) => void) | undefined;
			onError?: (error: ApiError) => void;
	  }
	| undefined;

export type UpdateBackendStoreUserMessages = (
	storeUserMessages: boolean,
	callbacks?: UpdateStoreUserMessagesCallbacks,
) => Promise<RovoUserSettings | undefined>;

type UseStoreUserMessagesPreferenceParams = {
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
};

export const useStoreUserMessagesPreference = ({
	assistanceServiceParams,
}: UseStoreUserMessagesPreferenceParams): {
	storeUserMessages: boolean;
	storeUserMessagesEligible: boolean;
	localStoreUserMessages: boolean;
	storeUserMessageMigratedToBE: boolean | undefined;
	setStoreUserMessages: (storeUserMessages: boolean) => void;
	markStoreUserMessageMigratedToBE: () => void;
	updateBackendStoreUserMessages: UpdateBackendStoreUserMessages;
} => {
	const {
		preferences,
		setStoreUserMessages: setLocalStoreUserMessages,
		markStoreUserMessageMigratedToBE,
	} = useUserPreferences();
	const useBackendPreference = fg('ai_mate_store_messages_be_driven');
	const { settings, updateSettings } = useRovoUserSettings({
		assistanceServiceParams,
		enabled: useBackendPreference,
	});

	const updateBackendStoreUserMessages = useCallback<UpdateBackendStoreUserMessages>(
		(storeUserMessages, callbacks) => updateSettings({ storeUserMessages }, callbacks),
		[updateSettings],
	);

	const setStoreUserMessages = useCallback(
		(storeUserMessages: boolean) => {
			// Keep the local value up to date as a rollback fallback while the
			// BE-driven mode is being rolled out.
			setLocalStoreUserMessages(storeUserMessages);

			if (useBackendPreference) {
				void updateBackendStoreUserMessages(storeUserMessages);
			}
		},
		[setLocalStoreUserMessages, updateBackendStoreUserMessages, useBackendPreference],
	);

	const backendSettingsLoaded = settings !== null;
	const backendStoreUserMessages = settings?.storeUserMessagesEligible
		? (settings.storeUserMessages ?? false)
		: false;
	const storeUserMessages =
		useBackendPreference && backendSettingsLoaded
			? backendStoreUserMessages
			: preferences.storeUserMessages;
	const storeUserMessagesEligible = useBackendPreference
		? (settings?.storeUserMessagesEligible ?? false)
		: fg('ai_mate_show_store_messages');

	return {
		storeUserMessages,
		storeUserMessagesEligible,
		localStoreUserMessages: preferences.storeUserMessages,
		storeUserMessageMigratedToBE: preferences.storeUserMessageMigratedToBE,
		setStoreUserMessages,
		markStoreUserMessageMigratedToBE,
		updateBackendStoreUserMessages,
	};
};

type StoreUserMessagesMigrationRunnerParams = {
	currentUserStoreMessagesPreference: boolean;
	isCurrentUserMigrated: boolean | undefined;
	markCurrentUserAsMigrated: () => void;
	updateBackendStoreUserMessages: UpdateBackendStoreUserMessages;
};

/**
 * Temporary one-shot migration from localStorage to BE.
 *
 * The parent renderer gates mounting with:
 *   - `ai_mate_store_messages_be_driven`: only run during the BE-backed rollout.
 *   - `ai_mate_show_store_messages`: same eligibility gate used historically by FE.
 *
 * This hook deliberately does not read BE settings or wait for `/user-settings` to
 * load. It only checks the current local preference and a browser-local sentinel.
 * BE defaults eligible users to `true`, so it only sends a partial PUT
 * `{ storeUserMessages: false }` for explicit local opt-outs. The backend merges
 * partial updates, so unrelated settings are preserved.
 */
export const StoreUserMessagesMigrationRunner = ({
	isCurrentUserMigrated,
	markCurrentUserAsMigrated,
	currentUserStoreMessagesPreference,
	updateBackendStoreUserMessages,
}: StoreUserMessagesMigrationRunnerParams): null => {
	const hasAttemptedRef = useRef(false);

	useEffect(() => {
		const migrated = isCurrentUserMigrated;
		if (hasAttemptedRef.current || migrated === undefined) {
			return;
		}

		if (migrated) {
			hasAttemptedRef.current = true;
			return;
		}

		// Only explicit local opt-outs need to be migrated because BE defaults eligible users to true.
		if (currentUserStoreMessagesPreference === false) {
			void updateBackendStoreUserMessages(false, {
				onSuccess: () => {
					hasAttemptedRef.current = true;
					markCurrentUserAsMigrated();
				},
			});
		} else {
			hasAttemptedRef.current = true;
			markCurrentUserAsMigrated();
		}
	}, [
		isCurrentUserMigrated,
		markCurrentUserAsMigrated,
		updateBackendStoreUserMessages,
		currentUserStoreMessagesPreference,
	]);

	return null;
};
